import {
  parseMarketOutcomes,
  parseOutcomePrices,
  isMarketResolved,
  normalizeBinaryProbabilityPair,
  isValidBinaryProbabilityPair,
} from '@/utils/market-outcomes';
import { serializeOutcomePrices } from '@/utils/market-collection';

function unwrapPricePayload(payload) {
  let raw = payload;
  if (raw == null || raw === '') return null;

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    if (Object.prototype.hasOwnProperty.call(raw, 'data') && raw.data != null) {
      raw = raw.data;
    }
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  return raw;
}

function lookupMapPrice(map, label) {
  if (!map || typeof map !== 'object') return NaN;
  const want = String(label ?? '').trim();
  if (!want) return NaN;
  if (Object.prototype.hasOwnProperty.call(map, want)) {
    const n = Number(map[want]);
    return Number.isFinite(n) ? n : NaN;
  }
  const wantLower = want.toLowerCase();
  for (const [key, value] of Object.entries(map)) {
    if (String(key).trim().toLowerCase() === wantLower) {
      const n = Number(value);
      return Number.isFinite(n) ? n : NaN;
    }
  }
  return NaN;
}

/**
 * 解析 GET /liyuan-trade/market/price 的 data。
 * FairOdds：优先 Map[outcome→price]；兼容旧版 JSON 数组（按下标对齐）。
 * @param {*} payload
 * @param {string[]|string} [outcomes] Map 时按 outcomes 顺序排成数组
 * @returns {number[]}
 */
export function parseMarketPricePayload(payload, outcomes = []) {
  const raw = unwrapPricePayload(payload);
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    return raw.map((v) => Number(v)).filter((n) => Number.isFinite(n));
  }

  if (typeof raw === 'object') {
    const keys = parseMarketOutcomes(outcomes);
    if (!keys.length) {
      return Object.values(raw)
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n));
    }
    return keys.map((label) => lookupMapPrice(raw, label));
  }

  return [];
}

/**
 * 批量询价响应 Map「{symbol}:{outcome}」→ 单 market 价格数组（按 outcomes 顺序）。
 * @param {Record<string, string|number>|null|undefined} batchMap
 * @param {string} symbol
 * @param {string[]|string} outcomes
 * @returns {number[]}
 */
export function pricesFromBatchPriceMap(batchMap, symbol, outcomes) {
  if (!batchMap || typeof batchMap !== 'object') return [];
  const sym = String(symbol ?? '').trim();
  if (!sym) return [];
  const keys = parseMarketOutcomes(outcomes);
  return keys.map((label) => {
    const want = `${sym}:${String(label ?? '').trim()}`;
    if (Object.prototype.hasOwnProperty.call(batchMap, want)) {
      const n = Number(batchMap[want]);
      return Number.isFinite(n) ? n : NaN;
    }
    const wantLower = want.toLowerCase();
    for (const [key, value] of Object.entries(batchMap)) {
      if (String(key).trim().toLowerCase() === wantLower) {
        const n = Number(value);
        return Number.isFinite(n) ? n : NaN;
      }
    }
    return NaN;
  });
}

/** 将轮询价格写回 markets[] 项的 outcomePrices 字段（已结算 / 已有 1/0 赢家不覆盖） */
export function applyMarketOutcomePrices(market, prices, collection) {
  if (!market || typeof market !== 'object') return false;
  if (isMarketResolved(market, collection)) return false;
  // 分钟盘未 closed 时也可能已有结算 1/0；禁止 /market/price 中间价抹掉赢家
  const settled = parseOutcomePrices(market.settlementOutcomePrices ?? market.outcomePrices);
  if (settled.some((p) => Number(p) === 1)) return false;
  const outcomes = parseMarketOutcomes(market.outcomes);
  if (!outcomes.length || !Array.isArray(prices) || prices.length < outcomes.length) {
    return false;
  }

  let normalized = prices.slice(0, outcomes.length);
  if (normalized.some((n) => !Number.isFinite(Number(n)))) return false;

  if (outcomes.length === 2) {
    normalized = normalizeBinaryProbabilityPair(normalized);
    if (!isValidBinaryProbabilityPair(normalized)) return false;
  }

  const next = serializeOutcomePrices(normalized);
  if (!next) return false;
  market.outcomePrices = next;
  return true;
}
